import React, { Component, CSSProperties, RefObject } from 'react'
import {
  CreateBarcodeValidation,
  ValidationLine,
} from '@getgreenline/homi-shared/dist/models/Payments/CreatePaymentLine'
import { IPaymentLine, IPaymentQueueLine, IPayment } from '@getgreenline/homi-shared'
import { Button } from 'antd'
import { observer } from 'mobx-react'
import { getTotalProductsToValidate } from '../../../../utilities/paymentsHelper'

interface Props<T extends IPaymentLine | IPaymentQueueLine> {
  createBarcodeValidation: CreateBarcodeValidation<T>
  paymentInfo: IPayment | null
  refValidationContainer: RefObject<HTMLDivElement>
}

@observer
export class CreateValidationLines<T extends IPaymentQueueLine | IPaymentLine> extends Component<
  Props<T>
> {
  get validationStatusText(): string {
    const barcodeValidation = this.props.createBarcodeValidation
    const totalItemCount = getTotalProductsToValidate(barcodeValidation)
    const validatedPaymentQueueLineLength = barcodeValidation.validationLines.filter(
      (line) =>
        line.isValidationRequired &&
        line.validationLine.productId &&
        barcodeValidation.validatedLines.includes(line.validationLine),
    ).length

    return `${validatedPaymentQueueLineLength} / ${totalItemCount} product${
      totalItemCount > 1 ? 's' : ''
    } validated`
  }

  containerStyle(validationLine: ValidationLine<T>): CSSProperties {
    const { areAllquantitiesValidated, quantityValidated } = validationLine
    let updateStyle: CSSProperties = {}

    switch (true) {
      case areAllquantitiesValidated === true:
        updateStyle = { ...styles.defaultContainer, ...styles.completed }
        break
      case quantityValidated !== 0:
        updateStyle = { ...styles.defaultContainer, ...styles.scanning }
        break
      default:
        updateStyle = styles.defaultContainer
    }

    return updateStyle
  }

  render() {
    const { createBarcodeValidation, refValidationContainer } = this.props
    const { validationLines } = createBarcodeValidation
    const linesNeedingValidation = validationLines.filter(
      (line) => line.isValidationRequired && line.validationLine.productId,
    )
    const totalItemsToValidate = getTotalProductsToValidate(createBarcodeValidation)

    return (
      <>
        {totalItemsToValidate > 0 ? (
          <>
            <p style={styles.caption}>{this.validationStatusText}</p>

            <div style={styles.productContainer} ref={refValidationContainer}>
              {linesNeedingValidation.map((line, index) => {
                const { validationLine, areAllquantitiesValidated } = line
                return (
                  <div key={`productValidationLine${index}`} style={this.containerStyle(line)}>
                    <p
                      style={validationLine.sku ? styles.name : { ...styles.name, ...styles.noSku }}
                    >
                      {validationLine.productName}
                    </p>

                    {validationLine.sku && <p>SKU: {validationLine.sku}</p>}

                    <div style={styles.actionContainer}>
                      <Button
                        disabled={areAllquantitiesValidated}
                        onClick={() => {
                          line.setQuantityValidated(line.maxQuantity)
                          line.setIsManuallyValidated(true)
                        }}
                      >
                        Manually validate
                      </Button>
                      <div>
                        <span style={styles.productScanned}>{`${line.quantityValidated} `}</span>
                        <span style={styles.productTotal}>/ {line.maxQuantity}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        ) : (
          <>
            <p style={styles.noProductText}>There is no product to validate.</p>
            <p style={styles.noProductText}>Press the apply button.</p>
          </>
        )}
      </>
    )
  }
}

const styles: { [key: string]: CSSProperties } = {
  caption: {
    marginBottom: 16,
    color: 'var(--secondary)',
    fontSize: 12,
    fontWeight: 500,
  },
  productContainer: {
    position: 'relative',
    maxHeight: 380,
    overflowY: 'scroll',
    scrollBehavior: 'smooth',
  },
  defaultContainer: {
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgba(0, 0, 0, 0.08)',
    padding: 12,
    marginBottom: 12,
    outline: 'none',
  },
  completed: {
    outline: '5px auto var(--success)',
  },
  scanning: {
    outline: '5px auto var(--orange)',
  },
  name: {
    fontSize: 16,
    color: 'var(--dark)',
    marginBottom: 4,
  },
  discountHeader: {
    fontSize: 16,
    color: 'var(--dark)',
    marginBottom: 10,
  },
  discountItem: {
    color: 'var(--dark)',
    marginBottom: 10,
  },
  noSku: {
    marginBottom: 12,
  },
  sku: {
    color: 'var(--secondary)',
    marginBottom: 12,
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  productScanned: {
    fontSize: 24,
    color: 'var(--dark)',
  },
  productTotal: {
    fontSize: 16,
    color: 'var(--secondary)',
  },
  noProductText: {
    fontSize: 16,
    textAlign: 'center',
    color: 'var(--secondary)',
    marginBottom: 0,
  },
}
